import { useCallback, useEffect, useRef, useState } from 'react';
import { queueNext, queueRelease } from '../api/client.js';

const RETRY_MS = 5000;
const COOLDOWN_MS = 60_000;
// Caller-side backstop: the server queue can name a guide who isn't a remote
// participant in this learner's lobby room (subscription lag / just left), so
// no ParticipantDisconnected ever fires. Slightly longer than the guide's 30s
// IncomingView auto-decline, so a present guide's decline normally wins first.
const RING_TIMEOUT_MS = 32_000;

export type QueueStatus = 'idle' | 'ringing' | 'waiting';

/** Drives the learner's auto-dial down the server queue over the existing invite
 *  flow. When `pendingPeer` clears while ringing: if `inCall` the call was
 *  accepted (release + idle); otherwise it was declined/no-answer → ring next. */
export function useCallQueue({ durationMinutes, topic, initiateCall, cancelCall, pendingPeer, inCall }: {
  durationMinutes: number;
  /** Optional topic the learner picked; carried on every ring, not just the first. */
  topic?: string;
  initiateCall: (guide: string, minutes: number, topic?: string) => void;
  cancelCall: () => void;
  pendingPeer: string | null;
  inCall: boolean;
}) {
  const [status, setStatus] = useState<QueueStatus>('idle');
  const statusRef = useRef<QueueStatus>('idle');
  const activeRef = useRef(false);
  const genRef = useRef(0); // per-attempt token; invalidates in-flight queueNext continuations
  const triedRef = useRef<Map<string, number>>(new Map()); // guide → last-tried ms
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ringTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const durationRef = useRef(durationMinutes); durationRef.current = durationMinutes;
  const topicRef = useRef(topic); topicRef.current = topic;
  const initiateRef = useRef(initiateCall); initiateRef.current = initiateCall;
  const cancelRef = useRef(cancelCall); cancelRef.current = cancelCall;

  const set = (s: QueueStatus) => { statusRef.current = s; setStatus(s); };
  const clearRetry = () => { if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; } };
  const clearRingTimeout = () => { if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; } };

  const ring = useCallback(async (gen: number) => {
    if (!activeRef.current || gen !== genRef.current) return;
    clearRetry();
    clearRingTimeout();
    const now = Date.now();
    const exclude = [...triedRef.current.entries()].filter(([, t]) => now - t < COOLDOWN_MS).map(([g]) => g);
    let guide: string | null = null;
    try { ({ guide } = await queueNext(exclude, { durationMinutes: durationRef.current, topic: topicRef.current })); }
    catch { if (activeRef.current && gen === genRef.current) { set('waiting'); retryRef.current = setTimeout(() => { void ring(gen); }, RETRY_MS); } return; }
    if (!activeRef.current || gen !== genRef.current) return;
    if (!guide) { set('waiting'); retryRef.current = setTimeout(() => { void ring(gen); }, RETRY_MS); return; }
    triedRef.current.set(guide, Date.now());
    set('ringing');
    initiateRef.current(guide, durationRef.current, topicRef.current);
    // Backstop: if the invite goes unanswered (no decline/disconnect event ever
    // arrives for a phantom/laggy guide), cancel it. Cancelling clears pendingPeer
    // → the outcome effect below advances to the next guide (reuses that path).
    ringTimeoutRef.current = setTimeout(() => {
      if (activeRef.current && genRef.current === gen && statusRef.current === 'ringing') cancelRef.current();
    }, RING_TIMEOUT_MS);
  }, []);

  const start = useCallback(() => {
    if (activeRef.current) return;
    activeRef.current = true;
    genRef.current++;
    const gen = genRef.current;
    triedRef.current = new Map();
    void ring(gen);
  }, [ring]);

  const stop = useCallback(() => {
    activeRef.current = false;
    genRef.current++; // invalidate any in-flight attempt
    clearRetry();
    clearRingTimeout();
    cancelCall();
    void queueRelease();
    set('idle');
  }, [cancelCall]);

  // Invite outcome: ringing → pendingPeer cleared.
  const prevPending = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevPending.current; prevPending.current = pendingPeer;
    if (!activeRef.current || statusRef.current !== 'ringing') return;
    if (prev && pendingPeer === null) {
      clearRingTimeout(); // outcome arrived (accept/decline/cancel) — drop the backstop
      if (inCall) { activeRef.current = false; genRef.current++; void queueRelease(); set('idle'); }
      else void ring(genRef.current);
    }
  }, [pendingPeer, inCall, ring]);

  // Release if we unmount mid-attempt (e.g. accept → CallView unmounts LobbyView).
  useEffect(() => () => {
    if (activeRef.current) void queueRelease();
    activeRef.current = false;
    genRef.current++; // invalidate any in-flight queueNext continuation
    clearRetry();
    clearRingTimeout();
  }, []);

  return { status, start, stop };
}
